import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  TrendingUp, DollarSign, Eye, MousePointer, ShoppingCart,
  AlertTriangle, CheckCircle, RefreshCw, Settings, Target,
  BarChart3, Zap, Users, Package, Play, Pause
} from 'lucide-react'
import { googleAdsApi, GoogleAdsCampaign, GoogleAdsAlert } from '@/lib/api'
import { useToast } from '@/components/ui/Toast'
import { cn } from '@/lib/utils'
import GoogleAdsConfigModal from '@/components/GoogleAdsConfigModal'

// Formatting helpers
const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(value)
}

const formatNumber = (value: number) => {
  return new Intl.NumberFormat('es-ES').format(Math.round(value))
}

const formatPercent = (value: number) => {
  return `${value.toFixed(1)}%`
}

// Severity styles for alerts
const severityStyles = {
  CRITICAL: {
    bg: 'bg-danger-50 dark:bg-danger-900/20',
    border: 'border-red-200 dark:border-red-800',
    icon: 'text-danger-600 dark:text-danger-400',
    text: 'text-red-800 dark:text-red-200'
  },
  WARNING: {
    bg: 'bg-orange-50 dark:bg-orange-900/20',
    border: 'border-orange-200 dark:border-orange-800',
    icon: 'text-orange-600 dark:text-orange-400',
    text: 'text-orange-800 dark:text-orange-200'
  },
  INFO: {
    bg: 'bg-primary-50 dark:bg-primary-900/20',
    border: 'border-blue-200 dark:border-blue-800',
    icon: 'text-primary-600 dark:text-primary-400',
    text: 'text-blue-800 dark:text-blue-200'
  }
}

// Campaign type icons
const campaignTypeIcons: Record<string, any> = {
  SHOPPING: ShoppingCart,
  PERFORMANCE_MAX: Zap,
  SEARCH: Eye,
  REMARKETING: Users
}

export default function GoogleAdsPage() {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [showConfigModal, setShowConfigModal] = useState(false)

  // Queries
  const statusQuery = useQuery({
    queryKey: ['google-ads-status'],
    queryFn: googleAdsApi.getStatus,
    refetchInterval: 60000
  })

  const configQuery = useQuery({
    queryKey: ['google-ads-config'],
    queryFn: googleAdsApi.getConfig
  })

  const performanceQuery = useQuery({
    queryKey: ['google-ads-performance'],
    queryFn: () => googleAdsApi.getPerformanceOverview(),
    enabled: statusQuery.data?.connected === true
  })

  const campaignsQuery = useQuery({
    queryKey: ['google-ads-campaigns'],
    queryFn: () => googleAdsApi.getCampaigns(),
    enabled: statusQuery.data?.connected === true
  })

  const alertsQuery = useQuery({
    queryKey: ['google-ads-alerts'],
    queryFn: () => googleAdsApi.getAlerts({ status: 'ACTIVE', limit: 10 }),
    enabled: statusQuery.data?.connected === true
  })

  const scoresQuery = useQuery({
    queryKey: ['google-ads-scores-summary'],
    queryFn: () => googleAdsApi.getProductScores({ limit: 5, recommendation: 'HIGHLY_RECOMMENDED' }),
    enabled: statusQuery.data?.connected === true
  })

  // Mutations
  const pauseCampaignMutation = useMutation({
    mutationFn: (campaignId: number) => googleAdsApi.pauseCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })
      toast.success('Campana pausada')
    },
    onError: (error: any) => {
      toast.error('Error pausando campana', error.response?.data?.detail || error.message)
    }
  })

  const enableCampaignMutation = useMutation({
    mutationFn: (campaignId: number) => googleAdsApi.enableCampaign(campaignId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })
      toast.success('Campana activada')
    },
    onError: (error: any) => {
      toast.error('Error activando campana', error.response?.data?.detail || error.message)
    }
  })

  const acknowledgeAlertMutation = useMutation({
    mutationFn: (alertId: number) => googleAdsApi.acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-alerts'] })
      toast.success('Alerta marcada como vista')
    }
  })

  const resolveAlertMutation = useMutation({
    mutationFn: (alertId: number) => googleAdsApi.resolveAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['google-ads-alerts'] })
      toast.success('Alerta resuelta')
    }
  })

  const isConnected = statusQuery.data?.connected === true

  const metrics = performanceQuery.data?.metrics

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white mb-2">
            Google Ads
          </h1>
          <p className="text-slate-600 dark:text-slate-400">
            Sistema de publicidad inteligente con optimizacion automatica
          </p>
        </div>
        <div className="flex gap-3">
          <button
            onClick={() => {
              queryClient.invalidateQueries({ queryKey: ['google-ads-status'] })
              queryClient.invalidateQueries({ queryKey: ['google-ads-performance'] })
              queryClient.invalidateQueries({ queryKey: ['google-ads-campaigns'] })
            }}
            className="flex items-center gap-2 px-4 py-2 bg-slate-100 dark:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg hover:bg-slate-200 dark:hover:bg-slate-600 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Sincronizar
          </button>
          <button
            onClick={() => setShowConfigModal(true)}
            className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            <Settings className="w-4 h-4" />
            Configurar
          </button>
        </div>
      </div>

      {/* Connection Status */}
      <div className={cn(
        "p-4 rounded-lg border flex items-center justify-between",
        isConnected
          ? "bg-success-50 dark:bg-success-900/20 border-green-200 dark:border-green-800"
          : "bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800"
      )}>
        <div className="flex items-center gap-3">
          {isConnected ? (
            <CheckCircle className="w-5 h-5 text-success-600 dark:text-success-400" />
          ) : (
            <AlertTriangle className="w-5 h-5 text-yellow-600 dark:text-yellow-400" />
          )}
          <div>
            <p className={cn(
              "font-medium",
              isConnected ? "text-green-800 dark:text-green-200" : "text-yellow-800 dark:text-yellow-200"
            )}>
              {isConnected
                ? `Conectado a Google Ads - ${statusQuery.data?.account_name || statusQuery.data?.customer_id}`
                : statusQuery.data?.error || "No conectado a Google Ads"
              }
            </p>
            {!isConnected && (
              <p className="text-sm text-yellow-600 dark:text-yellow-400">
                Configura las credenciales para conectar con tu cuenta
              </p>
            )}
          </div>
        </div>
        {isConnected && statusQuery.data?.currency && (
          <span className="text-sm text-success-600 dark:text-success-400">
            Moneda: {statusQuery.data.currency}
          </span>
        )}
      </div>

      {/* KPI Cards */}
      {isConnected && metrics && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Gasto */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">Gasto</span>
              <DollarSign className="w-5 h-5 text-danger-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {formatCurrency(metrics.cost)}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Ultimos 30 dias
            </p>
          </div>

          {/* Ingresos */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">Ingresos</span>
              <TrendingUp className="w-5 h-5 text-success-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {formatCurrency(metrics.conversion_value)}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Valor de conversiones
            </p>
          </div>

          {/* ROAS */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">ROAS</span>
              <Target className="w-5 h-5 text-primary-500" />
            </div>
            <p className={cn(
              "text-2xl font-bold",
              metrics.roas >= 400 ? "text-success-600" :
              metrics.roas >= 200 ? "text-yellow-600" : "text-danger-600"
            )}>
              {formatPercent(metrics.roas)}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Return on Ad Spend
            </p>
          </div>

          {/* Conversiones */}
          <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <span className="text-slate-500 dark:text-slate-400 text-sm font-medium">Conversiones</span>
              <ShoppingCart className="w-5 h-5 text-purple-500" />
            </div>
            <p className="text-2xl font-bold text-slate-900 dark:text-white">
              {formatNumber(metrics.conversions)}
            </p>
            <p className="text-sm text-slate-500 dark:text-slate-400 mt-1">
              Tasa: {formatPercent(metrics.conv_rate)}
            </p>
          </div>
        </div>
      )}

      {/* Additional Metrics Row */}
      {isConnected && metrics && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Impresiones</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(metrics.impressions)}
                </p>
              </div>
              <Eye className="w-8 h-8 text-slate-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">Clics</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatNumber(metrics.clicks)}
                </p>
              </div>
              <MousePointer className="w-8 h-8 text-slate-400" />
            </div>
          </div>
          <div className="bg-white dark:bg-slate-800 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-slate-500 dark:text-slate-400 text-sm">CTR / CPC</p>
                <p className="text-xl font-bold text-slate-900 dark:text-white">
                  {formatPercent(metrics.ctr)} / {formatCurrency(metrics.cpc)}
                </p>
              </div>
              <BarChart3 className="w-8 h-8 text-slate-400" />
            </div>
          </div>
        </div>
      )}

      {/* Main Content Grid */}
      {isConnected && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Alerts */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700 flex items-center justify-between">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Alertas Activas
              </h2>
              <span className="px-2 py-1 bg-danger-100 dark:bg-danger-900/30 text-danger-700 dark:text-red-300 text-sm rounded-full">
                {alertsQuery.data?.length || 0}
              </span>
            </div>
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {alertsQuery.data?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <CheckCircle className="w-12 h-12 mx-auto mb-3 text-success-500" />
                  <p>No hay alertas activas</p>
                </div>
              ) : (
                alertsQuery.data?.map((alert: GoogleAdsAlert) => {
                  const style = severityStyles[alert.severity]
                  return (
                    <div
                      key={alert.id}
                      className={cn("p-3 rounded-lg border", style.bg, style.border)}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-2">
                          <AlertTriangle className={cn("w-5 h-5 mt-0.5", style.icon)} />
                          <div>
                            <p className={cn("font-medium", style.text)}>{alert.title}</p>
                            {alert.message && (
                              <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
                                {alert.message}
                              </p>
                            )}
                            {alert.recommended_action && (
                              <p className="text-sm text-primary-600 dark:text-primary-400 mt-1">
                                Accion: {alert.recommended_action}
                              </p>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1">
                          <button
                            onClick={() => acknowledgeAlertMutation.mutate(alert.id)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                            title="Marcar como vista"
                          >
                            <Eye className="w-4 h-4 text-slate-500" />
                          </button>
                          <button
                            onClick={() => resolveAlertMutation.mutate(alert.id)}
                            className="p-1 hover:bg-slate-200 dark:hover:bg-slate-700 rounded"
                            title="Resolver"
                          >
                            <CheckCircle className="w-4 h-4 text-slate-500" />
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })
              )}
            </div>
          </div>

          {/* Campaigns */}
          <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
            <div className="p-4 border-b border-slate-200 dark:border-slate-700">
              <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
                Campanas
              </h2>
            </div>
            <div className="p-4 space-y-3 max-h-80 overflow-y-auto">
              {campaignsQuery.data?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 dark:text-slate-400">
                  <Package className="w-12 h-12 mx-auto mb-3" />
                  <p>No hay campanas</p>
                </div>
              ) : (
                campaignsQuery.data?.map((campaign: GoogleAdsCampaign) => {
                  const Icon = campaignTypeIcons[campaign.campaign_type] || Target
                  const isEnabled = campaign.status === 'ENABLED'
                  return (
                    <div
                      key={campaign.google_campaign_id}
                      className="p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <Icon className="w-5 h-5 text-primary-500" />
                          <span className="font-medium text-slate-900 dark:text-white">
                            {campaign.name}
                          </span>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={cn(
                            "px-2 py-0.5 text-xs rounded-full",
                            isEnabled
                              ? "bg-success-100 text-green-700 dark:bg-green-900/30 dark:text-green-300"
                              : "bg-slate-100 text-gray-700 dark:bg-gray-600 dark:text-slate-300"
                          )}>
                            {campaign.status}
                          </span>
                          <button
                            onClick={() => isEnabled
                              ? pauseCampaignMutation.mutate(campaign.google_campaign_id)
                              : enableCampaignMutation.mutate(campaign.google_campaign_id)
                            }
                            className={cn(
                              "p-1 rounded hover:bg-slate-200 dark:hover:bg-slate-600",
                              pauseCampaignMutation.isPending || enableCampaignMutation.isPending
                                ? "opacity-50 cursor-not-allowed"
                                : ""
                            )}
                            disabled={pauseCampaignMutation.isPending || enableCampaignMutation.isPending}
                          >
                            {isEnabled ? (
                              <Pause className="w-4 h-4 text-orange-500" />
                            ) : (
                              <Play className="w-4 h-4 text-success-500" />
                            )}
                          </button>
                        </div>
                      </div>
                      {campaign.metrics && (
                        <div className="grid grid-cols-3 gap-2 text-sm">
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Gasto:</span>
                            <span className="ml-1 text-slate-900 dark:text-white">
                              {formatCurrency(campaign.metrics.cost)}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">Conv:</span>
                            <span className="ml-1 text-slate-900 dark:text-white">
                              {campaign.metrics.conversions}
                            </span>
                          </div>
                          <div>
                            <span className="text-slate-500 dark:text-slate-400">ROAS:</span>
                            <span className={cn(
                              "ml-1 font-medium",
                              (campaign.metrics.roas || 0) >= 400 ? "text-success-600" :
                              (campaign.metrics.roas || 0) >= 200 ? "text-yellow-600" : "text-danger-600"
                            )}>
                              {campaign.metrics.roas ? formatPercent(campaign.metrics.roas) : '-'}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      )}

      {/* Top Products by AdScore */}
      {isConnected && scoresQuery.data?.products && scoresQuery.data.products.length > 0 && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 shadow-sm">
          <div className="p-4 border-b border-slate-200 dark:border-slate-700">
            <h2 className="text-lg font-semibold text-slate-900 dark:text-white">
              Productos Recomendados para Publicitar
            </h2>
            <p className="text-sm text-slate-500 dark:text-slate-400">
              Productos con mayor potencial segun AdScore
            </p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-slate-50 dark:bg-slate-700/50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    Producto
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    AdScore
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    Margen
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    Ventas 30d
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-slate-500 dark:text-slate-400 uppercase">
                    CPC Sugerido
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 dark:divide-slate-700">
                {scoresQuery.data.products.map((product) => (
                  <tr key={product.id_product} className="hover:bg-slate-50 dark:hover:bg-slate-700/30">
                    <td className="px-4 py-3">
                      <div>
                        <p className="font-medium text-slate-900 dark:text-white">
                          {product.product_name}
                        </p>
                        <p className="text-sm text-slate-500 dark:text-slate-400">
                          {product.reference}
                        </p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <div className="inline-flex items-center gap-2">
                        <div className="w-16 bg-slate-200 dark:bg-gray-600 rounded-full h-2">
                          <div
                            className={cn(
                              "h-2 rounded-full",
                              product.ad_score >= 80 ? "bg-success-500" :
                              product.ad_score >= 60 ? "bg-blue-500" :
                              product.ad_score >= 40 ? "bg-yellow-500" : "bg-danger-500"
                            )}
                            style={{ width: `${product.ad_score}%` }}
                          />
                        </div>
                        <span className="font-medium text-slate-900 dark:text-white">
                          {product.ad_score.toFixed(0)}
                        </span>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                      {product.margin_percent?.toFixed(1)}%
                    </td>
                    <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                      {product.sales_30d}
                    </td>
                    <td className="px-4 py-3 text-center text-slate-900 dark:text-white">
                      {product.suggested_cpc ? formatCurrency(product.suggested_cpc) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Not Connected State */}
      {!isConnected && (
        <div className="bg-white dark:bg-slate-800 rounded-xl border border-slate-200 dark:border-slate-700 p-8 text-center">
          <Target className="w-16 h-16 mx-auto mb-4 text-slate-400" />
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-2">
            Configura tu cuenta de Google Ads
          </h2>
          <p className="text-slate-600 dark:text-slate-400 mb-6 max-w-md mx-auto">
            Conecta tu cuenta de Google Ads para comenzar a gestionar tus campanas
            y obtener recomendaciones inteligentes basadas en tus productos y margenes.
          </p>
          <button
            onClick={() => setShowConfigModal(true)}
            className="px-6 py-3 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors font-medium"
          >
            Configurar Google Ads
          </button>
        </div>
      )}

      {/* Config Modal */}
      {showConfigModal && (
        <GoogleAdsConfigModal
          isOpen={showConfigModal}
          onClose={() => setShowConfigModal(false)}
          config={configQuery.data}
          onConfigured={() => {
            queryClient.invalidateQueries({ queryKey: ['google-ads-status'] })
            queryClient.invalidateQueries({ queryKey: ['google-ads-config'] })
            setShowConfigModal(false)
          }}
        />
      )}
    </div>
  )
}
